import { useEffect, useRef } from 'react'

import type { SalesOrder } from '@/lib/api/sales-order-api'
import type { PaymentCollectionContext } from '@/lib/payment-collection-tracking'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    amountChoice,
    sendOnceTracked,
} from '@/lib/payment-collection-tracking'

/** What the payment form holds; only its shape reaches analytics. */
export interface PaymentFormSnapshot {
    amount: unknown
    method: string
    reference?: string
}

interface MutateCallbacks {
    onSuccess: () => void
    onError: (error: unknown) => void
}

type OrderAtOpen =
    Pick<SalesOrder, 'settlement_intent' | 'payment_status'> | null | undefined

const FORM_DEFAULTS: PaymentCollectionContext = {
    paymentMethod: 'cash',
    hasReference: false,
    amountChoice: 'default',
}

function orderContext(order: OrderAtOpen): PaymentCollectionContext {
    return {
        settlementIntent: order?.settlement_intent ?? null,
        paymentStatus: order?.payment_status,
    }
}

function formContext(
    values: PaymentFormSnapshot,
    defaultAmount: number,
): PaymentCollectionContext {
    return {
        paymentMethod: values.method,
        hasReference: Boolean(values.reference?.trim()),
        amountChoice: amountChoice(values.amount, defaultAmount),
    }
}

/**
 * `order_payment_record`: the two ways money is recorded on the order detail
 * page — the "Ghi nhận thanh toán" dialog (one attempt per opening, the dialog
 * owns it) and the one-tap "Đánh dấu đã thanh toán" (one attempt per tap).
 * The order's intent and payment status are read when the attempt starts.
 */
export function useOrderPaymentTracking({
    open,
    order,
}: {
    open: boolean
    order: OrderAtOpen
}) {
    const dialog = useWorkflowTracking('order_payment_record', {
        active: open,
        attemptKey: 'record_dialog',
        operation: 'create',
    })
    const markPaid = useWorkflowTracking('order_payment_record', {
        autoStart: false,
        attemptKey: 'mark_paid',
        operation: 'create',
    })
    // Read at the moment an attempt starts, not on every refetch afterwards.
    const latestOrder = useRef(order)
    latestOrder.current = order

    useEffect(() => {
        if (open)
            dialog.setContext({
                entryPoint: 'record_dialog',
                ...orderContext(latestOrder.current),
                ...FORM_DEFAULTS,
            })
    }, [open, dialog])

    return {
        formChanged(values: PaymentFormSnapshot, defaultAmount: number) {
            dialog.interact('payment')
            dialog.setContext(formContext(values, defaultAmount))
        },
        formInvalid() {
            dialog.step('validation_failed')
        },
        submitDialog<T>(
            values: PaymentFormSnapshot,
            defaultAmount: number,
            send: () => Promise<T>,
        ): Promise<T> {
            dialog.setContext(formContext(values, defaultAmount))
            return dialog.run(send)
        },
        /** `send` must call the mutation with these callbacks exactly once. */
        markPaid(send: (callbacks: MutateCallbacks) => void) {
            sendOnceTracked(
                (execute) => {
                    markPaid.setContext({
                        entryPoint: 'mark_paid',
                        ...orderContext(order),
                        ...FORM_DEFAULTS,
                    })
                    return markPaid.run(execute)
                },
                () =>
                    new Promise<void>((resolve, reject) =>
                        send({ onSuccess: () => resolve(), onError: reject }),
                    ),
            )
        },
    }
}
